import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';
import ThemeToggle from '../components/ThemeToggle';

const LoginPage = () => {
  const { login, isAuthenticated, loading } = useAuth();
  const { tenant, tenantName } = useTenant();
  const { isDarkMode } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();

  const from = location.state?.from?.pathname || `/${tenantName}/dashboard`;

  useEffect(() => {
    if (isAuthenticated) {
      // Already logged in, redirect
    }
  }, [isAuthenticated]);

  if (loading) {
    return <div data-testid="loading-indicator">Loading...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      // Navigation will happen automatically due to isAuthenticated change
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundColor: isDarkMode ? '#1a1a1a' : '#f5f5f5',
      position: 'relative'
    }}>
      {/* Theme Toggle */}
      <div style={{
        position: 'absolute',
        top: '1rem',
        right: '1rem',
        zIndex: 1000
      }}>
        <ThemeToggle />
      </div>
      <div style={{
        backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: isDarkMode ? '0 2px 10px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '400px',
        border: isDarkMode ? '1px solid #404040' : 'none'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ margin: 0, color: isDarkMode ? '#ffffff' : '#333' }}>MiniBank</h1>
          <p style={{ margin: '0.5rem 0 0 0', color: isDarkMode ? '#b3b3b3' : '#666' }}>
            {tenant?.displayName || tenantName}
          </p>
        </div>

        <form onSubmit={handleSubmit} data-testid="login-form">
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: isDarkMode ? '#ffffff' : '#333' }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="email-input"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: isDarkMode ? '2px solid #404040' : '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
                color: isDarkMode ? '#ffffff' : '#333'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: isDarkMode ? '#ffffff' : '#333' }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="password-input"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: isDarkMode ? '2px solid #404040' : '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
                color: isDarkMode ? '#ffffff' : '#333'
              }}
            />
          </div>

          {error && (
            <div 
              data-testid="error-message"
              style={{
                backgroundColor: isDarkMode ? '#3a1e1e' : '#fee',
                color: isDarkMode ? '#ff6b6b' : '#c33',
                padding: '0.75rem',
                borderRadius: '4px',
                marginBottom: '1rem',
                fontSize: '0.9rem',
                border: isDarkMode ? '1px solid #ff6b6b' : 'none'
              }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            data-testid="submit-button"
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: isDarkMode ? '#4dabf7' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1rem',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={{ 
          marginTop: '2rem', 
          padding: '1rem', 
          backgroundColor: isDarkMode ? '#1e1e1e' : '#f8f9fa', 
          borderRadius: '4px',
          border: isDarkMode ? '1px solid #404040' : 'none'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: isDarkMode ? '#ffffff' : '#333' }}>Demo Credentials:</h4>
          <div style={{ fontSize: '0.85rem', color: isDarkMode ? '#b3b3b3' : '#666' }}>
            <div>Admin: admin@bank.test / password123</div>
            <div>User: user@bank.test / password123</div>
            <div>VIP: vip@bank.test / password123</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 